import { Controller } from "@hotwired/stimulus"
import { NativeBridge } from "../lib/native_bridge"

/**
 * NativeAuthController - Handle native OAuth authentication (LINE + Apple)
 *
 * Usage:
 *   <button data-controller="native-auth"
 *           data-action="click->native-auth#lineLogin">
 *     LINE Login
 *   </button>
 *
 *   <button data-controller="native-auth"
 *           data-action="click->native-auth#appleLogin">
 *     Apple Login
 *   </button>
 *
 * In native app:
 *   Intercepts click → calls NativeBridge.Auth.lineLogin() or appleLogin()
 *   → Native SDK authentication → Rails session cookie → WebView refresh
 *
 * In web browser:
 *   Falls through to normal form submission (OmniAuth flow)
 */
export default class extends Controller {
  static values = {
    inProgress: { type: Boolean, default: false },
    fallbackMessage: { type: String, default: "Switching login method..." }
  }

  connect() {
    // Hide Apple button on Android (most Android users don't have Apple accounts)
    if (NativeBridge.isAndroid() && this.element.dataset.action?.includes("appleLogin")) {
      this.element.style.display = "none"
    }
  }

  /**
   * Handle LINE login button click
   * In native app: start native LINE SDK flow
   * In browser: let form submission proceed
   */
  async lineLogin(event) {
    // Diagnostic logging for LINE login troubleshooting
    console.log("=== LINE LOGIN CLICK ===")
    console.log("  User Agent:", navigator.userAgent)
    console.log("  window.NativeBridge:", typeof window.NativeBridge)
    console.log("  isNative():", NativeBridge.isNative())

    // Only intercept in native app
    if (!NativeBridge.isNative()) {
      console.log("[NativeAuth] Not native app, using OmniAuth flow")
      return // Let default form submission proceed
    }

    // Prevent default form submission in native app
    event.preventDefault()
    event.stopPropagation()

    // Prevent double-clicks while login is in progress
    if (this.inProgressValue) {
      console.log("[NativeAuth] Login already in progress, ignoring click")
      return
    }

    console.log("[NativeAuth] Starting native LINE login")
    this.inProgressValue = true
    this.element.disabled = true

    try {
      const response = await NativeBridge.Auth.lineLogin()
      console.log("[NativeAuth] LINE login started:", response)
      // Native app will handle the result and reload WebView
    } catch (error) {
      console.error("[NativeAuth] LINE login failed:", error)
      this.inProgressValue = false
      this.element.disabled = false

      // Show brief message and fallback to OmniAuth
      this.fallbackToOmniAuth(error)
    }
  }

  /**
   * Handle Apple login button click (App Store Guideline 4.8)
   * In native app: start native Apple Sign In flow
   * In browser: let form submission proceed to OmniAuth
   */
  async appleLogin(event) {
    console.log("=== APPLE LOGIN CLICK ===")
    console.log("  User Agent:", navigator.userAgent)
    console.log("  isNative():", NativeBridge.isNative())
    console.log("  isIOS():", NativeBridge.isIOS())

    // Only intercept in native app
    if (!NativeBridge.isNative()) {
      console.log("[NativeAuth] Not native app, using OmniAuth Apple flow")
      return // Let default form submission proceed
    }

    // Prevent default form submission in native app
    event.preventDefault()
    event.stopPropagation()

    // Prevent double-clicks while login is in progress
    if (this.inProgressValue) {
      console.log("[NativeAuth] Login already in progress, ignoring click")
      return
    }

    console.log("[NativeAuth] Starting native Apple Sign In")
    this.inProgressValue = true
    this.element.disabled = true

    try {
      const response = await NativeBridge.Auth.appleLogin()
      console.log("[NativeAuth] Apple login started:", response)
      // Native app will handle the result and reload WebView
    } catch (error) {
      console.error("[NativeAuth] Apple login failed:", error)
      this.inProgressValue = false
      this.element.disabled = false

      // Show brief message and fallback to OmniAuth
      this.fallbackToOmniAuth(error)
    }
  }

  /**
   * Fallback to OmniAuth if native login fails
   * Shows a brief notification before redirecting
   */
  fallbackToOmniAuth(error) {
    console.log("[NativeAuth] Falling back to OmniAuth:", error?.message)

    // Show brief toast notification (message passed via data attribute for I18n)
    this.showToast(this.fallbackMessageValue)

    // Find the form and submit it after a brief delay
    const form = this.element.closest("form")
    if (form) {
      setTimeout(() => form.submit(), 300)
    } else {
      // If no form, try to find the action URL
      const action = this.element.getAttribute("formaction") ||
                     this.element.closest("[action]")?.getAttribute("action")
      if (action) {
        setTimeout(() => { window.location.href = action }, 300)
      }
    }
  }

  /**
   * Show a brief toast notification
   */
  showToast(message) {
    // Create toast element
    const toast = document.createElement("div")
    toast.className = "fixed bottom-20 left-1/2 -translate-x-1/2 bg-charcoal-800 text-white px-4 py-2 rounded-lg text-sm z-50 shadow-lg"
    toast.textContent = message
    document.body.appendChild(toast)

    // Remove after 2 seconds
    setTimeout(() => toast.remove(), 2000)
  }
}
